import { IJsonApiObject } from './general'

export interface Pagination {
  currentPage: number
  totalCount?: number
  limitValue?: number
}

export interface Shareable {
  id?: number
  type?: string
  is_published?: boolean
  share_uri?: string
  tweet_text?: string
  host?: string
  allow_embedding?: boolean
  can_read_permissions: boolean
  can_grant_group_permissions?: boolean
  can_grant_user_permissions?: boolean
}

export interface PublishableResource {
  id: number
  publish_status_code: number
}

export interface User {
  id: number
  display_name: string
  profile_link: string
}

export interface ManagedUserProps extends User {
  confirmed: boolean
}

export interface CurrentUserProps extends User {
  available_seats: number
  num_unpublished_timelines_available: number
  can_manage_account_users: boolean
  can_create_private: boolean
}

export interface Timeline {
  access_level: number
  photo_ids: number[]
  title: string
  start_date: string
  end_date: string
}

export interface Marker {
  id: number
  title: string
  description: string | null
  date: string
  photo_ids: number[]
}

export interface Timespan {
  id: number
  title: string
  description: string | null
  start_date: string
  end_date: string
}

export interface Group {
  id: number
  name: string
}

export interface Membership {
  id: number
  group_id: number
  user_id: number
}

export interface Permission {
  id: number
  permissable_type: string
  permissable_id: number
  permissionable_type: string
  permissionable_id: number
  role: string
}

export interface PermissionWithAbilities extends Permission {
  revocable: boolean
}

export interface Image {
  src: string
  width?: number
  height?: number
}

export interface ColorScheme {
  primary: string
  secondary: string
  tertiary: string
  highlight: string
  darkText: string
  lightText: string
}

export interface EditableDate {
  year?: string
  month?: string
  day?: string
  isBCE?: boolean
}

export interface ImageUrl {
  src: string
  width?: number
  height?: number
}

export type PhotoStyles =
  | 'medium'
  | 'small_square'
  | 'medium_square'
  | 'large_square'
  | 'tiny_landscape'
  | 'small_landscape'
  | 'medium_landscape'

type ImageUrls = {
  medium?: ImageUrl
  small_square?: ImageUrl
  medium_square?: ImageUrl
  large_square?: ImageUrl
  tiny_landscape?: ImageUrl
  small_landscape?: ImageUrl
  medium_landscape?: ImageUrl
}

export interface RestPhoto {
  id: number | string
  image_urls: ImageUrls
  image_processing_complete?: boolean
  created_at: string
  updated_at: string
}

export interface JsonapiPhoto extends IJsonApiObject {
  id: number | string
  attributes: {
    image_urls: ImageUrls
    image_processing_complete?: boolean
    created_at: string
    updated_at: string
  }
}
export type Photo = RestPhoto | JsonapiPhoto

// Type guards to differentiate between RestPhoto and JsonapiPhoto
export const isRestPhoto = (photo: Photo): photo is RestPhoto => 'image_urls' in photo
export const isJsonapiPhoto = (photo: Photo): photo is JsonapiPhoto => 'attributes' in photo

export interface Invitation {
  id?: number
  recipient_display_name?: string
  status?: string
}

// base table attributes
export interface TableEntity {
  created_at: string
  database_id: number
  name: string
  owner_id: number
  photo_id: number | null
  updated_at: string
  uuid: string
  effective_options: any
  access_level: number // internal access level (public/private)
  external_access_level?: number // external access level for sharing (public/private)
  access_state?: number // access state (active/limited/archived)
}

// editable table atrtibutes
export interface EditableTableEntity extends TableEntity {
  row_schema?: any
  customization_schema?: any
  customization_ui_schema?: any
}

// the table as serialized by the API
export interface Table extends IJsonApiObject {
  type: 'tables'
  attributes: TableEntity | EditableTableEntity
}

// Field variants (TODO: we should probably add serializers for these so we get have them in JSON::API format?)

// base field variant (common attributes across all variants)
interface FieldVariant {
  created_at: string
  updated_at: string
}

interface TextField extends FieldVariant {
  text_max_length?: number | null
}
type RichTextField = TextField

interface NumberField extends FieldVariant {
  number_decimal_places?: number | null
  number_type: 'integer' | 'decimal' | null
}

type FieldVariants = TextField | RichTextField | NumberField | FieldVariant

// Fields

export type FieldTypes =
  | 'TextField'
  | 'RichTextField'
  | 'NumberField'
  | 'TimelineField'
  | 'PhotoField'
  | 'SingleSelectField'
  | 'MultiSelectField'

export interface Field extends IJsonApiObject {
  id: string
  type: 'fields'
  attributes: {
    name: string
    variant_type: FieldTypes
    variant?: FieldVariants
    primary: boolean
    table_id: number
    position: number
    sortable: boolean
    filterable: boolean
    importable: boolean
  }
}

export interface FieldSelectOption extends IJsonApiObject {
  id: string
  type: 'field_select_options'
  attributes: {
    label: string
    field_id: number
    position: number
  }
  relationships: {
    field: {
      meta: {
        included: boolean
      }
    }
  }
}

export interface View extends IJsonApiObject {
  type: 'views'
  attributes: {
    name: string
    uuid: string
    table_id: string
    effective_options: any
    // Note: When serialized in editing mode, the following attributes are included
    external_access_level?: number
    customization_schema?: any
    customization_ui_schema?: any
    ui_schema?: any
  }
}

// Note: This is a generic type that can be used to represent any view variant
// E.g. TimelineView, TableView, etc.
export interface ViewVariant extends IJsonApiObject {
  type: 'view_variants'
  attributes: {
    // TBD: Will depend on the actual view variant
  }
}

export interface Row extends IJsonApiObject {
  type: 'rows'
  attributes: {
    position: number
    table_id: number
    values: any
  }
}
